import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DriverAPI } from './config';
import { getDriverLanguage, getDriverText } from './driverI18n';
import { getLocalizedDriverApiError } from './driverFeatureI18n';

const initialForm = {
  start_date: '',
  end_date: '',
  all_day: true,
  start_time: '09:00',
  end_time: '18:00',
  reason: '',
};

const formatTimeOff = (item, text) => {
  const dateText = item.start_date === item.end_date ? item.start_date : `${item.start_date} - ${item.end_date}`;
  if (item.all_day) return `${dateText} ${text.fullDay}`;
  return `${item.start_date} ${item.start_time}-${item.end_time}`;
};

export default function DriverTimeOffModal({ isOpen, onClose, api = DriverAPI }) {
  const [form, setForm] = useState(initialForm);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const language = getDriverLanguage();
  const text = getDriverText(language);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getTimeOff();
      setItems(data || []);
    } catch (error) {
      alert(getLocalizedDriverApiError(error, text.getTimeOffFailed, language));
    } finally {
      setLoading(false);
    }
  }, [api, language, text.getTimeOffFailed]);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen, fetchItems]);

  if (!isOpen) return null;

  const submit = async () => {
    if (!form.start_date) {
      alert(text.chooseStartDate);
      return;
    }
    if (!form.all_day && (!form.start_time || !form.end_time)) {
      alert(text.chooseStartEndTime);
      return;
    }

    setSaving(true);
    try {
      await api.createTimeOff({
        ...form,
        end_date: form.end_date || form.start_date,
      });
      setForm(initialForm);
      await fetchItems();
      alert(text.timeOffRegistered);
    } catch (error) {
      alert(getLocalizedDriverApiError(error, text.submitFailed, language));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (item) => {
    if (!window.confirm(`${text.confirmCancelTimeOff}\n${formatTimeOff(item, text)}`)) return;
    try {
      await api.cancelTimeOff(item.id);
      await fetchItems();
    } catch (error) {
      alert(getLocalizedDriverApiError(error, text.cancelFailed, language));
    }
  };

  return createPortal(
    <div
      className="driver-shared-modal fixed inset-x-0 top-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      style={{ minHeight: '100dvh' }}
      role="dialog"
      aria-modal="true"
    >
      <div className="premium-card max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-border-subtle bg-card/95 p-4">
          <div>
            <h3 className="text-lg font-bold">{text.timeOffRequest}</h3>
            <p className="text-xs text-muted-foreground">{text.timeOffHint}</p>
          </div>
          <button onClick={onClose} className="text-2xl text-muted-foreground hover:text-foreground">×</button>
        </div>

        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={text.startDate}>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })}
                className="premium-input"
              />
            </Field>
            <Field label={text.endDate}>
              <input
                type="date"
                value={form.end_date}
                disabled={!form.all_day}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="premium-input disabled:opacity-50"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 rounded-xl border border-border-subtle bg-surface p-3 text-sm">
            <input
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => setForm({ ...form, all_day: e.target.checked, end_date: e.target.checked ? form.end_date : form.start_date })}
            />
            {text.allDayRange}
          </label>

          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={text.startTime}>
                <input
                  type="time"
                  step="1800"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="premium-input"
                />
              </Field>
              <Field label={text.endTime}>
                <input
                  type="time"
                  step="1800"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="premium-input"
                />
              </Field>
            </div>
          )}

          <Field label={text.reason}>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="premium-input min-h-20"
              placeholder={text.reasonPlaceholder}
            />
          </Field>

          <button
            onClick={submit}
            disabled={saving}
            className="premium-button w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? text.submitting : text.submitTimeOff}
          </button>

          <div className="border-t border-border-subtle pt-4">
            <h4 className="mb-2 text-sm font-semibold">{text.myTimeOffRecords}</h4>
            {loading ? (
              <p className="text-sm text-muted-foreground">{text.loading}</p>
            ) : items.length === 0 ? (
              <p className="rounded-xl bg-surface p-4 text-center text-sm text-muted-foreground">{text.noTimeOffRecords}</p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.id} className="rounded-xl border border-border-subtle bg-surface p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{formatTimeOff(item, text)}</p>
                        {item.reason && <p className="mt-1 text-xs text-muted-foreground">{item.reason}</p>}
                        {item.status === 'cancelled' && <p className="mt-1 text-xs text-destructive">{text.cancelled}</p>}
                      </div>
                      {item.status === 'active' && (
                        <button
                          onClick={() => cancel(item)}
                          className="rounded-lg bg-destructive/10 px-2 py-1 text-xs text-destructive hover:bg-destructive/20"
                        >
                          {text.cancel}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
